import { FecImportAnalysisErrorCode } from '../enums/fecImportAnalysisErrorCode'
import { FecImportLifecycleStatus } from '../enums/fecImportLifecycleStatus'
import type { FecImportItem } from '../query-api'

// `Expected column "JournalCode" at position 1, got "X"`, also for optional columns. A row with too few columns
// (`Expected at least 18 columns, got 6`) names no column and is not matched.
const HEADER_COLUMN_POSITION_ERROR = /expected (optional )?column ".*" at position \d+/

const getAnalysisErrorText = (fecImport: FecImportItem): string => {
  const validationMessages = fecImport.errors?.map((error) => error.message) ?? []

  return [fecImport.errorMessage, fecImport.mappingErrorMessage, ...validationMessages]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
}

/**
 * The API gives no error code, only text, so the kind of failure is read from the wording. A timeout decides whether
 * "Retry analysis" is offered (see `isFecImportRetryable`); the other kinds only pick the copy. Anything unrecognised
 * is a parsing failure, which keeps "Reupload". If the backend rewords these messages, update the tests with them.
 *
 * The backend (account-import, `fec-import`) sends two fixed timeout texts, from its stuck-work recovery:
 * "Processing timed out — please retry" and "Mapping detection timed out — please retry". The rest is the parser's
 * validation text or the raw message of an exception.
 */
export const getFecImportAnalysisErrorCode = (fecImport: FecImportItem): FecImportAnalysisErrorCode => {
  const analysisErrorText = getAnalysisErrorText(fecImport)

  if (analysisErrorText.includes('timeout') || analysisErrorText.includes('timed out')) {
    return FecImportAnalysisErrorCode.Timeout
  }

  if (
    analysisErrorText.includes('encoding') ||
    analysisErrorText.includes('charset') ||
    analysisErrorText.includes('utf-8') ||
    analysisErrorText.includes('utf8') ||
    analysisErrorText.includes('iso-8859')
  ) {
    return FecImportAnalysisErrorCode.InvalidEncoding
  }

  // The file itself parsed: only the mapping detection failed, so it must not be reported as a corrupted file.
  if (fecImport.status === FecImportLifecycleStatus.DetectionFailed) {
    return FecImportAnalysisErrorCode.DetectionFailed
  }

  // "Header must have at least 18 DGFiP columns…", or a header column that is not where it should be.
  const isHeaderColumnError =
    (analysisErrorText.includes('column') &&
      (analysisErrorText.includes('header') || analysisErrorText.includes('missing'))) ||
    HEADER_COLUMN_POSITION_ERROR.test(analysisErrorText)

  return isHeaderColumnError ? FecImportAnalysisErrorCode.MissingColumns : FecImportAnalysisErrorCode.ParsingFailed
}
